import type { Locale } from '@/lib/locale';

/**
 * Flag artwork for the language selector, reproduced verbatim from the
 * flag-icons project (https://github.com/lipis/flag-icons, MIT) — the same
 * reasoning as provider-marks.tsx: artwork copied exactly from a maintained
 * source beats artwork redrawn from memory. The oddities are theirs and stay:
 * Germany's black is `#000001` (some toolchains treat pure black specially)
 * and the colors are the flags' official values, not "a" black/red/gold.
 *
 * Emoji flags were the first version. They are not glyphs a UI can rely on:
 * Windows has no flag emoji at all and renders 🇩🇪 as the letters "DE", and
 * every other platform draws its own art at its own weight.
 *
 * The paths are strings, not JSX, because the dropdown needs them inside
 * `<option>` elements via `dangerouslySetInnerHTML`: React 19's DOM-nesting
 * validator predates the customizable-select content model and logs a
 * console error for element children of an option, while innerHTML reaches
 * the same DOM without tripping it. The SVG ends up in the option's DOM in
 * EVERY browser (the fragment parser's context here is the option, which
 * has always allowed element children — the select-parser relaxation only
 * concerns select-context parsing); browsers without the customizable
 * picker simply never paint it, because a native picker renders only the
 * option's text. That is the whole fallback: same DOM, text-only rendering.
 *
 * The mark is always `aria-hidden`: on the selector's face the `<select>`
 * carries the state, and inside an option the language name is the content.
 * `data-flag` names the locale so tests can assert which flag is showing.
 */

const FLAG_PATHS: Record<Locale, string> = {
  en: [
    '<path fill="#012169" d="M0 0h640v480H0z"/>',
    '<path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0z"/>',
    '<path fill="#C8102E" d="m424 281 216 159v40L369 281zm-184 20 6 35L54 480H0zM640 0v3L391 191l2-44L590 0zM0 0l239 176h-60L0 42z"/>',
    '<path fill="#FFF" d="M241 0v480h160V0zM0 160v160h640V160z"/>',
    '<path fill="#C8102E" d="M0 193v96h640v-96zM273 0v480h96V0z"/>',
  ].join(''),
  de: [
    '<path fill="#fc0" d="M0 320h640v160H0z"/>',
    '<path fill="#000001" d="M0 0h640v160H0z"/>',
    '<path fill="red" d="M0 160h640v160H0z"/>',
  ].join(''),
};

export function FlagMark({
  locale,
  className,
}: {
  locale: Locale;
  className?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-flag={locale}
      viewBox="0 0 640 480"
      className={className}
      dangerouslySetInnerHTML={{ __html: FLAG_PATHS[locale] }}
    />
  );
}

/**
 * The flag + name markup for one `<option>`. The label is one of our own
 * copy-map constants, never user input — nothing here needs escaping.
 */
export function flagOptionHtml(locale: Locale, label: string): string {
  return (
    `<svg aria-hidden="true" focusable="false" data-flag="${locale}" ` +
    `viewBox="0 0 640 480" class="language-option-flag">` +
    `${FLAG_PATHS[locale]}</svg>${label}`
  );
}
